import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import { ProgressSpinner } from 'primereact/progressspinner';
import { Checkbox } from 'primereact/checkbox';

import { useAddCampaignContent, useAddPlaylistContent } from '../../../queries';
import { useScrollLoadingContext } from '../../Contexts/ScrollLoadingContext';
import { setLastElement } from '../../../redux/Slices/scrollSlice';
import { setDisableBtn } from '../../../redux/Slices/Global/globalSlice';
import ScrollContainer from '../ScrollContainer';
import SearchInput from './SearchInput';
import LazyImage from '../ShowMedia/LazyImage'
import AddContentSearchInput from './AddContentSearchInput';
import UploadPopup from '../../Media/components/UploadPopup/UploadPopup';

function LinksList({ builderRow = false }) {
    const { t } = useTranslation();
    const { isFetching, data, params } = useSelector((state) => state.scroll);
    const { assign, layer, campaignDetails } = useSelector((state) => state.global);
    const { setIds, ids } = useScrollLoadingContext();
    const screenDetails = useSelector((state) => state.layout.screenDetails);

    const addCampaignContent = useAddCampaignContent()
    const addPlaylistContent = useAddPlaylistContent()
    const lastElementRef = useRef(null);
    const dispatch = useDispatch();
    const { id } = useParams();

    const _params = {
        is_interactive: Number(campaignDetails?.is_interactive) ?? 0,
        section: "LINKS",
        layer: layer
    }

    useEffect(() => {
        if (ids?.length == 0) {
            dispatch(setDisableBtn(true))
        } else {
            dispatch(setDisableBtn(false))
        }
    }, [ids])

    useEffect(() => {
        if (assign) {
            dispatch(setDisableBtn(true))
            assignHandler()
        }
    }, [assign])

    const assignHandler = async () => {
        if (window.location.pathname.includes("campaign")) {
            await addCampaignContent.mutateAsync({
                "ids_list": ids,
                "campaign_id": id,
                "layer": layer,
                "type": "link",
            })
        }
        else if (window.location.pathname.includes("layout")) {
            if (!screenDetails?.id) {
                console.error("Screen details not loaded yet");
                dispatch(setDisableBtn(false));
                return;
            }
            await addCampaignContent.mutateAsync({
                "ids_list": ids,
                "campaign_id": screenDetails.id,
                "layer": layer,
                "type": "link",
            })
        }
        else {
            await addPlaylistContent.mutateAsync({
                "list_media_ids": ids,
                "playlist_id": id,
                "layer": layer,
            })
        }
    }

    const onMediaChange = (e) => {
        let selectedMedia = [...ids];

        if (e.checked)
            selectedMedia.push(e.value);
        else
            selectedMedia.splice(selectedMedia.indexOf(e.value), 1);

        (setIds(selectedMedia));
    }

    useEffect(() => {
        if (lastElementRef.current) {
            dispatch(setLastElement(lastElementRef.current));
        }
    }, [lastElementRef.current]);

    // ── Campaign builder add-content sheet — .pp-cb-yt-row-style rows,
    // ported from the prototype. This tab is a saved-links GALLERY (search
    // + multi-select from existing link-type media), not a bare "type a
    // URL" one-shot field — real, already-shipped functionality the
    // prototype's single-field mockup didn't model, so it's kept; only the
    // visual chrome changes. "Add a new link" still goes through the
    // existing UploadPopup entry point.
    if (builderRow) {
        // ⚠️ ScrollContainer was dropped entirely when this tab was restyled
        // — it's what actually fires the GET /media?section=LINKS fetch on
        // mount (same as the default render below). Without it, `data`
        // (state.scroll.data) never populates regardless of how many real
        // links exist, and the tab was stuck on the empty message forever.
        return (
            <div className="d-flex flex-column">
                <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="flex-1 min-w-0"><AddContentSearchInput isDisabled={data?.length === 0} /></div>
                    <UploadPopup content="links" />
                </div>
                <ScrollContainer path={`/media`} optionalParam={_params}>
                    {!isFetching && data?.length === 0 ? (
                        <div className="py-5 text-center w-100 opacity-70">
                            {isEmpty(params?.search) ? t('addcontent.no_links_yet') : t('messages.no_results')}
                        </div>
                    ) : (
                        data?.filter((item) => item.type !== 'HDMI').map((item, index) => {
                            const selected = ids.indexOf(`${item.id}`) !== -1;
                            const isLast = index === data.length - 1;
                            return (
                                <div
                                    key={item.id}
                                    ref={isLast ? lastElementRef : null}
                                    className="pp-cb-yt-row"
                                    style={selected ? { borderColor: 'var(--pp-btn-honey)', boxShadow: '0 0 0 1.5px var(--pp-btn-honey) inset' } : undefined}
                                    onClick={() => onMediaChange({ checked: !selected, value: item.id })}
                                >
                                    <span className="yt-th"><LazyImage media={item} popupType="media_preview" styleClass="w-100 h-100" /></span>
                                    <span className="yt-t">{item.file_name}</span>
                                    <span className="yt-d">{item.type}</span>
                                </div>
                            );
                        })
                    )}
                </ScrollContainer>
            </div>
        );
    }

    return (
        <>
            <div className="w-full my-3 px-3">
                <div className="grid grid-cols-[1fr_auto] items-center gap-2">
                    <div className="min-w-0"> {/* lets the input shrink */}
                        <AddContentSearchInput isDisabled={data?.length === 0} />
                    </div>
                    <div className="shrink-0">
                        <UploadPopup content="links" />
                    </div>
                </div>
            </div>

            <hr className="w-100 mt-0" />

            <ScrollContainer path={`/media`} optionalParam={_params}>
                {
                    !isFetching && data?.length == 0 ?
                        isEmpty(params?.search) ?
                            <h5 className="py-5 text-center w-100">{t('addcontent.no_links_yet')}</h5>
                            :
                            <h5 className="py-5 text-center w-100">{t('messages.no_results')}</h5>
                        :
                        <>
                            <div className="d-flex flex-column scroll_container scroll_div px-3 w-100">
                                <div className="d-flex flex-wrap mb-4">
                                    {
                                        data?.map((item, index) => {
                                            let imgEl = null;
                                            if (item.type != "HDMI") {
                                                imgEl = (
                                                    <LazyImage
                                                        media={item}
                                                        popupType={"media_preview"}
                                                        index={index}
                                                        styleClass={"pp-link-thumb-img"}
                                                    />
                                                );
                                            }
                                            return (
                                                item.type != "HDMI" &&
                                                (<div className={`w-100 d-flex align-items-center justify-content-between   py-2
                                                 ${ids.indexOf(`${item.id}`) !== -1 ? "bg-gray-200" : ""}
                                                  ${index + 1 != data?.length ? "border-bottom border-gray" : ""}  `}
                                                    ref={index === data?.length - 1 ? lastElementRef : null}
                                                    key={`${item.type} - ${index}`} >

                                                    <label htmlFor={item.id} className="col-11 d-flex align-items-center">
                                                        <div className="mx-2 pp-link-thumb"> {imgEl}  </div>
                                                        <div className="col-9 col-md-3 col-sm-6 mx-1 fs-6 d-flex align-items-center "> {item.file_name} </div>
                                                        <div className="col-4 d-flex align-items-center justify-content-center text-capitalize d-none d-sm-block fs-6 text-light fs-6">
                                                            {item.type}
                                                        </div>
                                                    </label>
                                                    <Checkbox inputId={item.id} name="media" className='me-3' value={item.id} onChange={onMediaChange} checked={ids.indexOf(`${item.id}`) !== -1} />
                                                </div>)
                                            )
                                        })
                                    }
                                    {
                                        isFetching && data?.length ?
                                            <ProgressSpinner
                                                className='h-[50px] w-[50px] flex justify-center items-center my-4'
                                                strokeWidth="3"
                                                fill="var(--surface-ground)"
                                                animationDuration="30s" /> : <></>
                                    }
                                </div>
                            </div>
                        </>
                }
            </ScrollContainer>
        </>
    );
}

export default LinksList